import type { CharacterMeshFormat } from '@cartwhl/3d/player';
import getMotion, {
  type MotionConfigSource,
} from '@cartwhl/common/api/getMotion';
import { triggerBrowserFileDownload } from '@cartwhl/common/utils';

export type { MotionConfigSource };

export interface MotionExportSettings {
  fps: number;
  frameStep: number;
  rootMotion: boolean;
}

export interface CharacterMeshDownloadProfile {
  downloadType: 'fbx' | 'gltf';
  label: string;
  fileExtension: string;
}

export interface DownloadCharacterMotionExportOptions {
  apiKey: string | null;
  characterID: string | null;
  motionID: string | null;
  /** Zero-based body index for multi-person comic jobs. */
  bodyIndex?: number;
  configSource: MotionConfigSource;
  meshDownloadProfile: CharacterMeshDownloadProfile | null;
  settings: MotionExportSettings;
  userMotionUrl: string | null;
  filename: string;
  prepareForUserMotionRequest?: (options: {
    configSource: MotionConfigSource;
    mode: 'auto' | 'manual';
  }) => Promise<boolean> | boolean;
}

export interface DownloadCharacterMotionExportResult {
  ok: boolean;
  error?: string;
}

export const DEFAULT_MOTION_EXPORT_SETTINGS: MotionExportSettings = {
  fps: 30,
  frameStep: 1,
  rootMotion: true,
};

/** Allowed GET /motion `fps` values (matches external Mogen API). */
export const MOTION_DOWNLOAD_FPS_OPTIONS = [
  2, 12, 23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60, 90, 100, 120, 144, 240,
] as const;

/** Safe single segment for `cartwheel-{character}-{motion}.bvh`. */
export function sanitizeFileNameSegment(
  input: string,
  fallback: string,
): string {
  const s = input
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return s || fallback;
}

export function resolveMotionNameSlug(options: {
  externalMotionLabel: string | null;
  effectiveMotionId: string | null;
}): string {
  const { externalMotionLabel, effectiveMotionId } = options;
  if (externalMotionLabel?.trim()) {
    return sanitizeFileNameSegment(externalMotionLabel.trim(), 'motion');
  }
  const id = effectiveMotionId?.trim();
  if (id) {
    return sanitizeFileNameSegment(id, 'motion');
  }
  return 'motion';
}

import { inferMeshFormatFromPath } from '@cartwhl/3d/r3f/characterMeshFormat';

export { inferMeshFormatFromPath };

/**
 * Character-native motion download: API uses `gltf` for both .glb and .gltf assets;
 * label reflects the user's actual file type for the button.
 */
export function getCharacterMeshDownloadProfile(
  meshFormat: CharacterMeshFormat | undefined,
  meshPath: string,
): CharacterMeshDownloadProfile | null {
  const fmt = meshFormat ?? inferMeshFormatFromPath(meshPath);
  if (fmt === 'fbx')
    return { downloadType: 'fbx', label: 'FBX', fileExtension: 'fbx' };
  if (fmt === 'glb')
    return { downloadType: 'gltf', label: 'GLB', fileExtension: 'glb' };
  if (fmt === 'gltf')
    return { downloadType: 'gltf', label: 'GLTF', fileExtension: 'gltf' };
  return null;
}

export function buildCharacterMotionExportFilename(options: {
  characterID: string | null;
  userCharacterDisplayName: string | null;
  motionID: string | null;
  motionLabel: string | null;
  fileExtension: string;
}) {
  const userCharacterSlug = sanitizeFileNameSegment(
    options.userCharacterDisplayName?.trim() ||
      options.characterID?.trim() ||
      'character',
    'character',
  );
  const motionNameSlug = resolveMotionNameSlug({
    externalMotionLabel: options.motionLabel,
    effectiveMotionId: options.motionID,
  });
  return `${userCharacterSlug}-${motionNameSlug}.${options.fileExtension}`;
}

export { triggerBrowserFileDownload };

export async function downloadCharacterMotionExport({
  apiKey,
  characterID,
  motionID,
  bodyIndex,
  configSource,
  meshDownloadProfile,
  settings,
  userMotionUrl,
  filename,
  prepareForUserMotionRequest,
}: DownloadCharacterMotionExportOptions): Promise<DownloadCharacterMotionExportResult> {
  if (
    !meshDownloadProfile ||
    !apiKey ||
    !characterID ||
    !motionID ||
    !userMotionUrl
  ) {
    return { ok: false, error: 'Motion export is not ready yet.' };
  }

  if (configSource === 'temp' && prepareForUserMotionRequest) {
    const didPrepare = await prepareForUserMotionRequest({
      configSource,
      mode: 'manual',
    });
    if (!didPrepare) {
      return { ok: false };
    }
  }

  const { data, error } = await getMotion(motionID, apiKey, {
    characterID,
    ...(configSource === 'temp' ? { configSource } : {}),
    downloadType: meshDownloadProfile.downloadType,
    fps: settings.fps,
    frameStep: settings.frameStep,
    rootMotion: settings.rootMotion,
    ...(bodyIndex !== undefined ? { bodyIndex } : {}),
  });
  if (error || !data) {
    return { ok: false, error: error?.message ?? 'Download failed' };
  }

  const url =
    meshDownloadProfile.downloadType === 'fbx' ? data.fbxURL : data.gltfURL;
  if (!url) {
    return {
      ok: false,
      error: 'No file URL returned for this motion.',
    };
  }

  await triggerBrowserFileDownload(url, filename);
  return { ok: true };
}
